import { Injectable } from '@angular/core';
import { Chats } from '@local/client-contracts';
import { observable } from '@local/common';
import { isFileSupported } from '@local/ts-infra';
import { uniq } from 'lodash';
import { filter, Observable } from 'rxjs';
import { BlobsService } from 'src/app/bar/services/blobs/blob.service';
import { BaseFile, BlobsStorageService, StorageUpdated } from 'src/app/bar/services/blobs/blobs-storage.service';
import { ItemsCache } from 'src/app/bar/services/items-cache';
import { ItemsCacheService } from 'src/app/bar/services/items-cache.service';
import { ShowToasterService } from 'src/app/bar/services/show-toaster.service';
import { AcceptFileConstants } from '../accept-file.constants';
@Injectable()
export class ChatFileService {
  private readonly ID_KEY_CACHE = 'blobId';
  private readonly CACHE_TYPE = 'chat-files';
  private readonly TOASTER_ID = 'chat-file-error';

  private cacheFiles: ItemsCache<BaseFile>;

  readonly MAX_FILE_SIZE_MB = 15;
  readonly MAX_FILE_SIZE_BYTES = this.MAX_FILE_SIZE_MB * 1024 * 1024;
  readonly MAX_FILE_UPLOADED_ON_THREAD = 20;

  getAcceptedFiles(): string[] {
    return Object.keys(AcceptFileConstants.ACCEPTED_FILES).map((ext) => `.${ext}`);
  }

  constructor(
    private itemsCacheService: ItemsCacheService,
    private blobsService: BlobsService,
    private blobsStorageService: BlobsStorageService<BaseFile>,
    private showToasterService: ShowToasterService
  ) {
    this.cacheFiles = this.itemsCacheService.getItemsCache(this.CACHE_TYPE);
  }

  getChatFileQueryParams(chatId: string, experienceId: string) {
    return `&chatId=${chatId}&experienceId=${experienceId}`;
  }

  onUploadFiles(files: File[], chatId: string, totalFilesInChat = 0): File[] {
    const checked = this.getValidFiles(files);
    if (checked.error) {
      this.onUploadError(checked.error);
    }
    if (!checked.validFiles?.length) {
      return [];
    }
    const maxUploadedFiles = this.MAX_FILE_UPLOADED_ON_THREAD - totalFilesInChat;
    if (checked.validFiles.length > maxUploadedFiles) {
      const excessFiles = checked.validFiles
        .slice(maxUploadedFiles)
        .map((file) => file.name)
        .join(', ');
      const excessFilesMessage = excessFiles.replace(/, ([^,]*)$/, ' and $1');
      this.onUploadError(`${excessFilesMessage} couldn't be uploaded. Thread exceeds ${this.MAX_FILE_UPLOADED_ON_THREAD} files limit.`);
    }
    return checked.validFiles.slice(0, maxUploadedFiles);
  }

  onUploadError(message: string) {
    this.showToasterService.showErrorToaster(this.TOASTER_ID, message);
  }

  private getValidFiles(files: File[]): {
    validFiles: File[];
    error: string;
  } {
    const validFiles: File[] = [];
    const failedFiles: { name: string; reason: 'type' | 'size' }[] = [];
    let error: string;

    files.forEach((file) => {
      const isValidType = isFileSupported(
        file,
        Object.values(AcceptFileConstants.ACCEPTED_FILES),
        Object.keys(AcceptFileConstants.ACCEPTED_FILES)
      );
      const isValidSize = file.size <= this.MAX_FILE_SIZE_BYTES;

      if (isValidType && isValidSize) {
        validFiles.push(file);
      } else {
        failedFiles.push({ name: file.name, reason: isValidSize ? 'type' : 'size' });
      }
    });

    if (failedFiles.length) {
      if (failedFiles.length === 1) {
        const file = failedFiles[0];
        const fileSizeLimitExceededError = `exceeds the ${this.MAX_FILE_SIZE_MB}MB size limit.`;
        error = `${file.name} couldn't be uploaded because its ${file.reason === 'type' ? 'file type is unsupported.' : fileSizeLimitExceededError}`;
      } else {
        const fileNames = failedFiles.map((file) => file.name).join(', ');
        error = `Some files couldn't be uploaded: ${fileNames}`;
      }
    }

    return { validFiles, error };
  }

  //storage files - before send message
  deleteFile(following: string, blobId: string, queryParams: string = '') {
    this.blobsStorageService.deleteAttachment(following, blobId, queryParams);
  }

  getFiles(chatId: string) {
    return this.blobsStorageService.getAttachmentsByFollowingId(chatId);
  }

  resetFiles(chatId: string, blobIds: string[], queryParams: string) {
    this.blobsStorageService.resetAttachments(chatId, blobIds, queryParams);
  }

  @observable
  updatedFileStorage$(chatId: string): Observable<StorageUpdated> {
    return this.blobsStorageService.updatedStorageByFollowingId$.pipe(filter((storage) => storage.followingId === chatId));
  }

  //cache files - after send message
  private getFilesBlobIds(chatHistory: Chats.ChatHistoryItem[]): string[] {
    const blobIds = (chatHistory || [])
      .map((h) => h?.userMessage?.blobIds || [])
      .filter((r) => !!r)
      .flat();
    return uniq(blobIds);
  }

  async updateRemoteFiles(chatHistory: Chats.ChatHistoryItem[]) {
    const blobIds = this.getFilesBlobIds(chatHistory);
    const files = await this.blobsService.getFilesByBlobIds(blobIds);
    this.updateFilesInCache(files);
  }

  updateFilesInCache(files: BaseFile[]) {
    return this.cacheFiles.updateItemsInCache(files, this.ID_KEY_CACHE);
  }

  @observable
  getFilesByBlobIds$(blobIds: string[]): Observable<BaseFile[]> {
    return this.cacheFiles.getItemsByIds$(blobIds);
  }
}
